const ZUKO_ID_ATTRIBUTE = 'data-zuko-id';
const DATA_TEST_ATTRIBUTE = 'data-test';

const getDataTestAttributeElem = (element) => {
  if (element?.hasAttribute(DATA_TEST_ATTRIBUTE)) {
    return element;
  }

  const children = element?.children;

  if (!children) {
    return null;
  }

  for (const child of children) {
    // Recursively check each child and return value if found
    const value = getDataTestAttributeElem(child);
    if (value !== null) {
      return value;
    }
  }

  return null; // If no "data-test" attribute is found in the entire subtree
};

/**
 * The `v-add-zuko-tag` directive is used to add and manage Zuko analytics attributes to components for tracking user interactions.
 * It based on data-test attributes and clones them.
 * Supports custom params if difference with data-test attribute required.
 */
export const addZukoTag = {
  inserted (component, binding) {
    const dataTestElement = getDataTestAttributeElem(component);

    if (dataTestElement === null) {
      console.log(`Please define ${DATA_TEST_ATTRIBUTE} attribute`, component);
      return;
    }

    const zukoIdValue = binding?.value || dataTestElement.getAttribute(DATA_TEST_ATTRIBUTE);

    /*
    * To make Zuko properly read user interactions with vuetify (v.2.4.2) v-select we need to add
    * the attribute data-zuko-tag-name="select" to the first parent element of the input
    * used in v-select, as v-select is composed of multiple divs and an input.
     * */
    const isSelect = component.classList.contains('v-select') && !component.classList.contains('v-autocomplete');
    if (isSelect) {
      const element = component.querySelector('.v-select__selections');
      element.setAttribute('data-zuko-tag-name', 'select');
      element.setAttribute(ZUKO_ID_ATTRIBUTE, zukoIdValue);
      return;
    }

    /*
    * For autocomplete data-test attribute applied to root div element
    * when Zuko required it to be on an actual input with selected value
    * */
    const isAutocomplete = component.classList.contains('v-autocomplete');
    if (isAutocomplete) {
      const element = component.querySelector('input[type="text"]');
      element.setAttribute(ZUKO_ID_ATTRIBUTE, zukoIdValue);
      return;
    }

    dataTestElement.setAttribute(ZUKO_ID_ATTRIBUTE, zukoIdValue);
  },
  // used for reactive updates with custom bindings
  update (component, binding) {
    if (!binding?.value || !binding?.oldValue || binding.value === binding.oldValue) return;

    const dataTestElement = getDataTestAttributeElem(component);

    if (dataTestElement === null) {
      console.log(`Please define ${DATA_TEST_ATTRIBUTE} attribute`, component);
      return;
    }

    const zukoIdValue = binding?.value || dataTestElement.getAttribute(DATA_TEST_ATTRIBUTE);
    component.setAttribute(ZUKO_ID_ATTRIBUTE, zukoIdValue);
  },
};
